<template>
  <PageWrapper
    :active-section="SECTION_PUBLICATIONS"
    :active-page="publication.name"
    :breadcrumbs="publicationBreadCrumbs"
    page-name="Overview"
    align="left"
    width="full"
  >
    <ProfileWrapper
      :splash-image="publicationSplashImage"
      :logo-image="publicationLogo"
      :is-loading-splash="loadingUploadSplashImage"
      can-upload-logo
      can-upload-splash
      can-edit-title
      can-edit-details
      has-menu
      :wrap-menu="false"
      :is-loading-logo="isUploadingAnyLogo"
      :title="publicationName"
      :details="publicationDescription || 'Click here to add a description for your publication'"
      @click:save-title="savePublicationName"
      @click:save-details="savePublicationDescription"
      @click:splash-image="triggerUploadSplash"
      @click:logo-image="openEditLogoModal"
    >
      <iSpace :height="10" />
      <template #menu>
        <iDropdownMenu
          v-if="canManagePublication"
          :items="dropdownItems"
          :show-dropdown="isDropdownOpen"
          :teleport-to-body="false"
          position="right"
          @close="closeDropdown"
          @click="updateStatus"
        >
          <template #trigger>
            <iIcon icon="dots-vertical" @click="openDropdown" />
          </template>
        </iDropdownMenu>
      </template>
      <iRow class="metric-content">
        <iColumn width="hug" gap="extraSmall">
          <iSubHeading font-weight="dark">
            Visibility
          </iSubHeading>
          <iCheckbox
            :model-value="visibleInCommunity"
            label="Visible In Community"
            name="visible_in_community"
            @change="toggleVisibility"
          />
          <iLink class="publication-profile-link" :href="publicationProfileUrl">
            Visit Profile
          </iLink>
        </iColumn>

        <iColumn width="hug" gap="extraSmall">
          <iSubHeading font-weight="dark">
            Statistics
          </iSubHeading>
          <iText>{{ totalUsers }} {{ totalUsers === 1 ? 'user' : 'users' }}</iText>
          <iText>{{ totalSubscribers }} {{ totalSubscribers === 1 ? 'subscriber' : 'subscribers' }}</iText>
        </iColumn>

        <iColumn width="hug" gap="extraSmall">
          <iSubHeading font-weight="dark">
            Posts
          </iSubHeading>
          <iText>{{ totalPublishedPosts }} published {{ totalPublishedPosts === 1 ? 'post' : 'posts' }}</iText>
          <iText>{{ totalDraftPosts }} draft {{ totalDraftPosts === 1 ? 'post' : 'posts' }}</iText>
          <iText v-if="hasLastPublishedPublication">
            {{ `Last Published: ${lastPublishedPostTimestamp}` }}
          </iText>
          <iLink
            v-if="canEditStories"
            color="subtle"
            target="_blank"
            :href="managePostsUrl"
          >
            Manage Posts
          </iLink>
        </iColumn>

        <iColumn width="hug" gap="extraSmall">
          <iSubHeading font-weight="dark">
            Production
          </iSubHeading>
          <iText variant="subtle">
            Status: {{ mappedPublicationStatus }}
          </iText>
          <iLink
            target="_blank"
            :href="internalDomain"
          >
            {{ internalDomain }}
          </iLink>

          <iSpace :height="1" />
          <iSubHeading font-weight="dark">
            Staging
          </iSubHeading>
          <iText variant="subtle">
            Status: {{ publicationStagingStatus }}
          </iText>
          <iLink
            v-if="canEditPublication || canEditStories"
            target="_blank"
            :href="internalDesignDomain"
          >
            Design & Preview
          </iLink>
        </iColumn>
      </iRow>
    </ProfileWrapper>
    <iUploader ref="splash-uploader" @upload-file="uploadSplash" />
    <EditPublicationLogoModal
      :visible="isEditLogoModalOpen"
      @close="closeEditLogoModal"
    />
    <AddToNetworkModal />
  </PageWrapper>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";
import { useNetworkStore } from "@/stores/network-store";
import PageWrapper from "@/components/PageWrapper";
import {
  PAGE_PUBLICATION_DASHBOARD,
  SECTION_PUBLICATIONS,
} from "@/constants/nav-constants";
import ProfileWrapper from "@/components/publication/ProfileWrapper";
import EditPublicationLogoModal from "@/components/publication/EditPublicationLogoModal.vue";
import AddToNetworkModal from "@/components/networks/AddToNetworkModal.vue";
import { dateFromUnixTimestamp, formatDateString } from "@bloglovin/vue-framework";
import { MenuConstants } from "@vcl";
import { useGeneralStore } from "%/stores/general-store";
import { useCollaboratorStore } from "~/stores/collaborator-store";

export default {
  name: "ViewPublicationDetails",
  components: {
    ProfileWrapper,
    PageWrapper,
    EditPublicationLogoModal,
    AddToNetworkModal,
  },
  data() {
    return {
      PAGE_PUBLICATION_DASHBOARD,
      SECTION_PUBLICATIONS,
      isDropdownOpen: false,
      isEditLogoModalOpen: false,
    };
  },
  computed: {
    ...mapState(useCreatorPublicationStore, [
      "publication",
      "publicationId",
      "orgId",
      "publicationLogo",
      "publicationSplashImage",
      "publicationName",
      "publicationDescription",
      "isLoadingSavePublicationName",
      "isLoadingSavePublicationDescription",
      "isUploadingAnyLogo",
      "loadingUploadSplashImage",
    ]),
    ...mapState(useNetworkStore, [
      "ownedNetworks",
    ]),
    ...mapState(useCollaboratorStore, [
      "canEditStories",
      "canEditPublication",
      "canManagePublication",
    ]),
    visibleInCommunity() {
      return this.publication.visibility == "visible";
    },
    domain() {
      return this.publication.domain;
    },
    publicationStagingStatus() {
      if (this.publication.staged_changes > 0) {
        return `${this.publication.staged_changes} updates waiting to be deployed`;
      }
      return "Up to date";
    },
    publicationProfileUrl() {
      return `${useGeneralStore().links.web}/community/publication/${this.publication.publication_id}/profile`;
    },
    publicationBreadCrumbs() {
      return [
        { url: "/", label: "Home" },
        { url: `/publication/org/${this.orgId}`, label: "My Publications" },
      ];
    },
    totalUsers() {
      return this.publication.metrics.filter(metric => metric.id === "users")?.[0]?.value || 0;
    },
    mappedPublicationStatus() {
      switch (this.publication.status) {
        case "active":
          return "Live";
        case "draft":
          return "Under Construction";
        default:
          return "Offline";
      }
    },
    totalSubscribers() {
      return this.publication.metrics.filter(metric => metric.id === "subscribers")?.[0]?.value || 0;
    },
    domainStaging() {
      if (this.domain) {
        if (this.domain.startsWith("http", 0)) {
          return this.domain;
        }
        return "https://" + this.domain + "/staging";
      }
      return "No domain set";
    },
    domainUrl() {
      if (this.domain) {
        if (this.domain.startsWith("http", 0)) {
          return this.domain;
        }
        return "https://" + this.domain;
      }
      return "No domain set";
    },
    internalDomain() {
      return `${window.location.origin}/destination/${this.publicationId}`;
    },
    internalStagingDomain() {
      return `${window.location.origin}/staging/${this.publicationId}`;
    },
    internalDesignDomain() {
      return `${window.location.origin}/design/${this.publicationId}`;
    },
    publicProfileUrl() {
      return `${window.location.origin}/community/publication/${this.publicationId}/profile`;
    },
    managePostsUrl() {
      return `${window.location.origin}/publication/${this.publicationId}/stories`;
    },
    hasDomain() {
      return this.internalDomain !== this.domain;
    },
    hasLastPublishedPublication() {
      return !!this.publication.publication_post_stats.last_post_published_at;
    },
    totalPublishedPosts() {
      return this.publication.publication_post_stats.num_published_posts;
    },
    totalDraftPosts() {
      return this.publication.publication_post_stats.num_draft_posts;
    },
    lastPublishedPostTimestamp() {
      const date = dateFromUnixTimestamp(this.publication.publication_post_stats.last_post_published_at);
      return formatDateString(date, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    dropdownItems() {
      let deactivateMenuItem = {
        name: "inactive",
        label: "De-activate",
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "inactive",
      };
      let items = [];
      if (this.ownedNetworks.length > 0) {
        items.push({
          name: "add_to_network",
          label: "Add To Network",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "add_to_network",
        });
      }
      if (this.publication.status === "draft") {
        items.push(deactivateMenuItem);
        return items;
      }
      items = items.concat([
        {
          name: "active",
          label: "Activate",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "active",
        },
        deactivateMenuItem,
      ]).filter(item => {
        if (["active", "inactive"].includes(item.name)) {
          return this.publication.status !== item.name;
        }
        return true;
      });
      return items;
    },
  },
  methods: {
    ...mapActions(useCreatorPublicationStore, [
      "uploadSplashImage",
      "savePublicationName",
      "savePublicationDescription",
      "updatePublicationStatus",
      "updatePublicationVisibility",
    ]),
    ...mapActions(useNetworkStore, [
      "openAddToNetworkModal",
    ]),
    triggerEditPublicationName() {
      this.isEditNameOpen = true;
    },
    triggerEditPublicationDescription() {
      this.isEditNameOpen = true;
    },
    openEditLogoModal() {
      this.isEditLogoModalOpen = true;
    },
    closeEditLogoModal() {
      this.isEditLogoModalOpen = false;
    },
    triggerUploadSplash() {
      this.$refs["splash-uploader"].$refs.file.click();
    },
    uploadSplash(file) {
      this.uploadSplashImage(file);
    },
    openDropdown() {
      this.isDropdownOpen = true;
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    updateStatus(status) {
      if (status === "add_to_network") {
        this.openAddToNetworkModal(this.publicationId);
      } else {
        this.updatePublicationStatus(status);
      }
      this.closeDropdown();
    },
    toggleVisibility(visibility) {
      this.updatePublicationVisibility(visibility !== true ? "hidden" : "visible");
    },
  },
  styleGuide: () => ({
    logoBorderWidth: { "size.border": "thick" },
    logoBorderColor: { "color.border": "dark" },
    logoBackground: { "color.background": "light" },
    menuLinkHorizontalPadding: { "size.spacing": "standard" },
  }),
};
</script>

<style scoped lang="scss">
.publication-header-wrapper{
  position: relative;
}

.publication-header {
  position: relative;
  bottom: 30px;
}

.metric-content {
  gap: 56px !important;
}

.publication-profile-link {
  margin-left: 32px;

  &:hover {
    margin-left: 32px;
  }
}

.publication-header-logo {
  border: v-bind("$getStyles.logoBorderWidth") solid v-bind("$getStyles.logoBorderColor");
  background: v-bind("$getStyles.logoBackground")
}
:deep(.button-text-link){
  padding-left: v-bind("$getStyles.menuLinkHorizontalPadding") !important;
  padding-right: v-bind("$getStyles.menuLinkHorizontalPadding") !important;
}
</style>
